import * as RadixSelect from "@radix-ui/react-select";
import { CaretDown, Check } from "@phosphor-icons/react";
import { forwardRef, Fragment, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn, twTransform } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional group heading. Options sharing a `group` render under one heading,
   * in first-seen group order. Options without a group render ungrouped. */
  group?: string;
};

/**
 * Controls the option popover width relative to the trigger.
 * - `"trigger"` — exactly the trigger width.
 * - `"auto"` — at least the trigger width, growing to fit content (can be wider).
 * - a number — a fixed pixel width.
 */
export type SelectMenuWidth = "trigger" | "auto" | number;

/** Groups a flat option list by `group`, preserving first-seen order. */
export function groupSelectOptions(
  options: SelectOption[]
): { group?: string; items: SelectOption[] }[] {
  const groups: { group?: string; items: SelectOption[] }[] = [];
  const indexByGroup = new Map<string | undefined, number>();
  for (const option of options) {
    const key = option.group;
    if (!indexByGroup.has(key)) {
      indexByGroup.set(key, groups.length);
      groups.push({ group: key, items: [] });
    }
    groups[indexByGroup.get(key)!].items.push(option);
  }
  return groups;
}

export type SelectProps = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Option popover width relative to the trigger. Defaults to `"auto"`. */
  menuWidth?: SelectMenuWidth;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md";
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

type SelectSize = "sm" | "md";

interface SelectTriggerOwnerState {
  size: SelectSize;
  error: boolean;
}

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build, except the trigger active ring/outline was removed so
 * focused/open select fields render a single border. */

const triggerBase: CSSObject = {
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".5rem",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  textAlign: "left",
  fontWeight: 400,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "none",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    WebkitTextFillColor: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  "&[data-placeholder]": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  }
};

const triggerSizeStyles: Record<SelectSize, CSSObject> = {
  sm: {
    borderRadius: "7px",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    fontSize: "12px",
    height: "1.75rem"
  },
  md: {
    borderRadius: "7px",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    fontSize: "12px",
    height: "2.25rem"
  }
};

const triggerError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[data-state="open"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
};

const triggerDefaultBorder = (size: SelectSize): CSSObject => ({
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[data-state="open"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  ...(size === "sm"
    ? {
        "&[data-filled]": {
          borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
        }
      }
    : null)
});

const SelectTrigger = styled(RadixSelect.Trigger, {
  label: "AgenticSelect-trigger",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: SelectTriggerOwnerState }>(({ ownerState }) => [
  triggerBase,
  triggerSizeStyles[ownerState.size],
  ownerState.error ? triggerError : triggerDefaultBorder(ownerState.size)
]);

const SelectValue = styled("span", {
  label: "AgenticSelect-value"
})({
  display: "block",
  minWidth: "0px",
  flex: "1 1 0%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const SelectIcon = styled(RadixSelect.Icon, {
  label: "AgenticSelect-icon"
})({
  flexShrink: 0,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SelectCaret = styled(CaretDown, {
  label: "AgenticSelect-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const SelectContent = styled(RadixSelect.Content, {
  label: "AgenticSelect-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { menuWidth: SelectMenuWidth } }>(({ ownerState }) => [
  {
    zIndex: 1000,
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-lg)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    ...kfContentIn,
    animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  ownerState.menuWidth === "trigger" && { width: "var(--radix-select-trigger-width)" },
  ownerState.menuWidth === "auto" && { minWidth: "var(--radix-select-trigger-width)" },
  { maxHeight: "min(20rem,var(--radix-select-content-available-height))" }
]);

const SelectViewport = styled(RadixSelect.Viewport, {
  label: "AgenticSelect-viewport"
})({
  padding: ".25rem"
});

const SelectItem = styled(RadixSelect.Item, {
  label: "AgenticSelect-item"
})({
  display: "flex",
  width: "100%",
  cursor: "pointer",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  userSelect: "none",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".75rem",
  borderRadius: "var(--bfrs-radius-sm)",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&[data-disabled]": {
    pointerEvents: "none",
    cursor: "default",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  },
  "&[data-highlighted]": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  },
  '&[data-state="checked"]': {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    fontWeight: 700
  },
  height: "1.75rem",
  paddingLeft: ".625rem",
  paddingRight: ".625rem",
  paddingTop: ".375rem",
  paddingBottom: ".375rem",
  fontSize: "12px"
});

const SelectItemText = styled(RadixSelect.ItemText, {
  label: "AgenticSelect-itemText"
})({
  minWidth: "0px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const SelectItemIndicator = styled("span", {
  label: "AgenticSelect-itemIndicator"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const SelectGroupLabel = styled(RadixSelect.Label, {
  label: "AgenticSelect-groupLabel"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingBottom: ".25rem",
  paddingTop: ".5rem",
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      placeholder = "Select",
      open: controlledOpen,
      defaultOpen = false,
      onOpenChange,
      menuWidth = "auto",
      error,
      size = "md",
      className,
      id,
      value,
      defaultValue,
      onValueChange,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const menuWidthStyle = typeof menuWidth === "number" ? { width: menuWidth, minWidth: menuWidth } : undefined;
    const renderItem = (option: SelectOption) => (
      <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
        <SelectItemText>{option.label}</SelectItemText>
        <SelectItemIndicator>
          <RadixSelect.ItemIndicator>
            <Check size={13} weight="bold" />
          </RadixSelect.ItemIndicator>
        </SelectItemIndicator>
      </SelectItem>
    );
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<string | undefined>(defaultValue);
    const currentValue = isControlled ? value : internalValue;
    const selectedLabel = options.find((option) => option.value === currentValue)?.label;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const currentOpen = controlledOpen ?? uncontrolledOpen;

    const valueRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const node = valueRef.current;
      if (!node || typeof globalThis.ResizeObserver === "undefined") return;
      const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth + 1);
      measure();
      const observer = new globalThis.ResizeObserver(measure);
      observer.observe(node);
      return () => observer.disconnect();
    }, [selectedLabel]);

    const handleValueChange = (next: string) => {
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
    };

    const handleOpenChange = (nextOpen: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    };

    const trigger = (
      <SelectTrigger
        ref={ref}
        id={id}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid ?? error}
        data-filled={currentValue && !error ? "" : undefined}
        ownerState={{ size, error: Boolean(error) }}
        className={className}
      >
        <SelectValue ref={valueRef}>
          <RadixSelect.Value placeholder={placeholder} />
        </SelectValue>
        <SelectIcon>
          <SelectCaret ownerState={{ open: Boolean(currentOpen) }} size={10} />
        </SelectIcon>
      </SelectTrigger>
    );

    return (
      <RadixSelect.Root value={value} defaultValue={defaultValue} open={currentOpen} onOpenChange={handleOpenChange} onValueChange={handleValueChange} {...props}>
        {selectedLabel ? (
          <Tooltip content={selectedLabel} disabled={!isTruncated}>
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )}
        <RadixSelect.Portal>
        <BfrsPortalScope>
          <SelectContent
            position="popper"
            sideOffset={6}
            collisionPadding={floatingCollisionPadding}
            style={menuWidthStyle}
            ownerState={{ menuWidth }}
          >
            <SelectViewport>
              {groupSelectOptions(options).map((grp, groupIndex) =>
                grp.group ? (
                  <RadixSelect.Group key={`group-${groupIndex}`}>
                    <SelectGroupLabel>{grp.group}</SelectGroupLabel>
                    {grp.items.map(renderItem)}
                  </RadixSelect.Group>
                ) : (
                  <Fragment key={`group-${groupIndex}`}>{grp.items.map(renderItem)}</Fragment>
                )
              )}
            </SelectViewport>
          </SelectContent>
        </BfrsPortalScope>
      </RadixSelect.Portal>
    </RadixSelect.Root>
    );
  }
);

Select.displayName = "Select";
